<template>
  <div
    ref="wrapper"
    :class="['relative w-full', isFullscreen ? 'fixed inset-0 z-50 bg-white' : '']"
    :style="isFullscreen ? '' : 'height: calc(100vh - 12rem)'"
  >
    <!-- Loading -->
    <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="text-2xl text-gray-400" />
    </div>

    <!-- Error state -->
    <div
      v-if="loadError"
      class="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
    >
      <div class="text-center text-gray-500 pointer-events-auto">
        <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="text-4xl mb-2 text-red-300" />
        <p class="text-sm mb-2">{{ loadError }}</p>
        <button
          class="text-xs text-primary hover:underline bg-transparent border-0 cursor-pointer"
          @click="fetchData"
        >
          Retry
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!loading && !loadError && filteredMarkers.length === 0"
      class="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
    >
      <div class="text-center text-gray-500 pointer-events-auto">
        <font-awesome-icon :icon="['fas', 'map']" class="text-4xl mb-2 text-gray-300" />
        <p class="text-sm mb-1">No practices match the current filters.</p>
        <p class="text-xs text-gray-400">Try adjusting the filters in the left panel.</p>
        <button
          v-if="activeFilters"
          class="mt-2 text-xs text-primary hover:underline bg-transparent border-0 cursor-pointer"
          @click="clearAllFilters"
        >
          Clear all filters
        </button>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="absolute top-4 right-4 z-10 flex flex-wrap items-center justify-end gap-2 max-w-[calc(100%-280px)]">
      <!-- Search -->
      <div ref="searchWrapper" class="relative">
        <input
          ref="searchInput"
          v-model="searchQuery" data-demo-search
          type="text"
          autocomplete="off"
          name="network-map-search"
          placeholder="Search practices..."
          aria-label="Search practices"
          class="sensitive text-xs border border-gray-300 rounded px-2.5 py-1.5 bg-white shadow-sm w-48 pr-7"
          @focus="searchFocused = true"
          @blur="onSearchBlur"
          @keydown.down.prevent="searchHighlight = Math.min(searchHighlight + 1, searchResults.length - 1)"
          @keydown.up.prevent="searchHighlight = Math.max(searchHighlight - 1, 0)"
          @keydown.enter.prevent="onSearchEnter"
          @keydown.escape="searchFocused = false"
        />
        <font-awesome-icon
          v-if="!searchQuery"
          :icon="['fas', 'magnifying-glass']"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"
        />
        <button
          v-if="searchQuery"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer p-0 leading-none"
          aria-label="Clear search"
          title="Clear search"
          @mousedown.prevent="clearSearch"
        >
          <font-awesome-icon :icon="['fas', 'xmark']" class="text-[10px]" />
        </button>
        <!-- Search dropdown -->
        <div
          v-if="showSearchDropdown"
          class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto"
          style="width: 280px; max-height: 240px; z-index: 50"
        >
          <div
            v-if="searchResults.length === 0"
            class="px-3 py-3 text-xs text-gray-400 text-center"
          >
            No practices found
          </div>
          <div
            v-for="(result, idx) in searchResults"
            :key="idx"
            :class="[
              'px-3 py-2 text-xs cursor-pointer',
              idx === searchHighlight ? 'bg-gray-100' : 'hover:bg-gray-50',
            ]"
            @mousedown.prevent="jumpToSearchResult(result)"
            @mouseenter="searchHighlight = idx"
          >
            <div class="font-medium text-gray-800 truncate sensitive" v-html="highlightMatch(result.name)"></div>
            <div v-if="showLegalName(result)" class="text-gray-500 truncate">
              <span class="text-gray-400">Legal:</span> <span class="sensitive" v-html="highlightMatch(result.legal_name)"></span>
            </div>
            <div v-if="showDba(result)" class="text-gray-500 truncate">
              <span class="text-gray-400">DBA:</span> <span class="sensitive" v-html="highlightMatch(result.dba)"></span>
            </div>
            <div v-if="result.address" class="text-gray-400 truncate sensitive" v-html="highlightMatch(result.address)"></div>
          </div>
          <div
            v-if="searchResults.length === 15"
            class="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100 text-center sticky bottom-0 bg-white"
          >
            Showing top 15 &mdash; type more to narrow results
          </div>
        </div>
      </div>

      <!-- Tile style -->
      <select
        v-model="tileStyleIndex"
        class="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white shadow-sm"
        title="Change map background style"
      >
        <option v-for="(style, i) in tileStyles" :key="style.label" :value="i">
          Map: {{ style.label }}
        </option>
      </select>

      <!-- Color by -->
      <select
        v-model="colorByField"
        class="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white shadow-sm"
        title="Color practice bubbles by this field"
      >
        <option v-for="opt in colorByOptions" :key="opt.field" :value="opt.field">
          Color: {{ opt.label }}
        </option>
      </select>

      <!-- Size by -->
      <select
        v-model="sizeByField"
        class="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white shadow-sm"
        title="Size practice bubbles by this field"
      >
        <option v-for="opt in sizeByOptions" :key="opt.label" :value="opt.field">
          Size: {{ opt.label }}
        </option>
      </select>

      <!-- Grayscale toggle -->
      <button
        :class="[
          'text-xs border rounded px-2.5 py-1.5 shadow-sm cursor-pointer transition-colors',
          grayscale
            ? 'bg-gray-800 text-white border-gray-800'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
        ]"
        title="Toggle black & white mode (useful for printing)"
        @click="grayscale = !grayscale"
      >
        B&amp;W
      </button>

      <!-- Include terminated toggle -->
      <button
        :class="[
          'text-xs border rounded px-2.5 py-1.5 shadow-sm cursor-pointer transition-colors',
          includeTerminated
            ? 'bg-gray-800 text-white border-gray-800'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
        ]"
        title="Terminated practices are hidden by default. Click to show them with a red border."
        @click="includeTerminated = !includeTerminated"
      >
        Show Terminated
      </button>

      <!-- Export image -->
      <div class="shadow-sm rounded-md">
        <ExportMenu :disabled="!markers.length" :exports="[{ type: 'chart', label: 'Export Image', action: exportPng }]" />
      </div>

      <!-- Practice list panel toggle (practices currently displayed) -->
      <button
        :class="[
          'text-xs border rounded px-2.5 py-1.5 shadow-sm cursor-pointer transition-colors',
          showPracticeList
            ? 'bg-gray-800 text-white border-gray-800'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
        ]"
        title="Show the list of practices currently displayed on the map"
        @click="showPracticeList = !showPracticeList"
      >
        <font-awesome-icon :icon="['fas', 'list']" class="mr-1" />
        Practice List
      </button>

      <!-- Fullscreen -->
      <button
        class="text-xs border border-gray-300 rounded px-2.5 py-1.5 bg-white shadow-sm hover:bg-gray-50 cursor-pointer"
        :title="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
        :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
        @click="toggleFullscreen"
      >
        <font-awesome-icon :icon="['fas', isFullscreen ? 'arrows-minimize' : 'arrows-maximize']" />
      </button>
    </div>

    <!-- Filter sidebar -->
    <NetworkMapFilters
      ref="filtersPanel"
      :markers="markers"
      :visible-count="filteredMarkers.length"
      :total-count="markers.length"
      @update:filters="onFiltersChange"
    />

    <!-- Legend -->
    <div
      v-if="legendItems.length"
      class="absolute bottom-4 right-4 z-10 bg-white/90 rounded-lg shadow border border-gray-200 p-3 max-h-56 overflow-y-auto"
    >
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Legend</span>
        <button
          v-if="hasColorOverrides"
          class="text-[10px] text-primary hover:underline bg-transparent border-0 cursor-pointer ml-2"
          title="Reset all bubble colors to the default palette"
          @click="resetColors"
        >
          Reset colors
        </button>
      </div>
      <label
        v-for="item in legendItems"
        :key="item.label"
        class="flex items-center gap-2 text-xs text-gray-700 mb-1 last:mb-0 cursor-pointer"
        title="Click the swatch to pick a custom color"
      >
        <span class="relative w-3 h-3 rounded-full shrink-0" :style="{ background: item.color }">
          <input
            type="color"
            :value="item.color"
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            @input="onLegendColorInput(item, $event)"
          />
        </span>
        <span class="truncate max-w-[140px]">{{ item.label }}</span>
      </label>

      <!-- Primary vs additional location distinction (shown when additional locations are visible) -->
      <div
        v-if="activeFilters && activeFilters.location_type === 'All'"
        class="mt-2 pt-2 border-t border-gray-200"
      >
        <div class="flex items-center gap-2 text-xs text-gray-700 mb-1">
          <span class="w-3 h-3 rounded-full shrink-0 bg-gray-500 border-2 border-white"></span>
          <span>Primary location</span>
        </div>
        <div class="flex items-center gap-2 text-xs text-gray-700">
          <span class="w-3 h-3 rounded-full shrink-0 bg-gray-300 border-2 border-white"></span>
          <span>Additional location (lighter shade)</span>
        </div>
      </div>
      <div v-if="includeTerminated" class="mt-2 pt-2 border-t border-gray-200">
        <div class="flex items-center gap-2 text-xs text-gray-700">
          <span class="w-3 h-3 rounded-full shrink-0 bg-gray-400 border-2 border-red-600"></span>
          <span>Terminated</span>
        </div>
      </div>
    </div>

    <!-- Basemap attribution (required by CARTO free-tier terms) -->
    <div
      v-if="!loading && !loadError"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-white/90 rounded-lg shadow border border-gray-200 px-2 py-1 pointer-events-none"
    >
      <span class="text-[10px] text-gray-500">
        ©
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener"
          class="hover:underline pointer-events-auto"
        >OpenStreetMap contributors</a>, ©
        <a
          href="https://carto.com/attributions"
          target="_blank"
          rel="noopener"
          class="hover:underline pointer-events-auto"
        >CARTO</a>
      </span>
    </div>

    <!-- Practice list panel (currently displayed practices) -->
    <div
      v-if="showPracticeList"
      class="absolute bottom-4 left-4 z-10 bg-white rounded-lg shadow-lg border border-gray-200 flex flex-col"
      style="width: 320px; max-height: 50%"
    >
      <div class="flex items-center justify-between px-3 py-2 border-b border-gray-200 shrink-0">
        <span class="text-xs font-semibold text-gray-700">
          Practices shown ({{ filteredMarkers.length }})
        </span>
        <div class="flex items-center gap-2">
          <button
            class="text-xs text-primary hover:underline bg-transparent border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!filteredMarkers.length"
            title="Download as CSV"
            @click="exportPracticeList"
          >
            <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
            CSV
          </button>
          <button
            class="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer"
            @click="showPracticeList = false"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" class="text-xs" />
          </button>
        </div>
      </div>
      <div class="overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="sticky top-0 bg-gray-50 text-gray-500">
            <tr>
              <th class="text-left font-medium px-3 py-1.5">Practice Name</th>
              <th class="text-left font-medium px-3 py-1.5">Legal Name</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!filteredMarkers.length">
              <td colspan="2" class="px-3 py-3 text-center text-gray-400">No practices match the current filters.</td>
            </tr>
            <tr
              v-for="m in sortedFilteredMarkers"
              :key="m.entity_id"
              class="border-t border-gray-100 hover:bg-primary/5 cursor-pointer"
              title="Click to jump to this practice on the map"
              @click="navigateToMarker(m)"
            >
              <td class="px-3 py-1.5 text-gray-800">
                {{ m.name }}
                <div v-if="showDba(m)" class="text-[10px] text-gray-400 leading-tight">{{ m.dba }}</div>
              </td>
              <td class="px-3 py-1.5 text-gray-500">{{ m.legal_name || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Map container -->
    <div
      ref="mapContainer"
      :class="['w-full h-full overflow-hidden border border-gray-200', isFullscreen ? '' : 'rounded-lg']"
      :style="grayscale ? 'filter: grayscale(1)' : ''"
    ></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { tile as d3Tile } from 'd3-tile';
import axios from 'axios';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import NetworkMapFilters from './NetworkMapFilters.vue';
import {
  COLOR_BY_OPTIONS,
  SIZE_BY_OPTIONS,
  TILE_STYLES,
  buildColorScale,
  labelFor,
} from './networkMapColors';
import { beginSensitiveEgress, blockSensitiveEgress } from '@/utils/demoEgress';
import demoProtectedFields from '@/mixins/demoProtectedFields';

const COLOR_OVERRIDE_PREFIX = 'network_map_colors';

export default {
  name: 'NetworkMap',
  components: { ExportMenu, NetworkMapFilters, FontAwesomeIcon },
  mixins: [demoProtectedFields],
  // The practice search identifies real practices. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['searchQuery'],
  data() {
    return {
      loading: true,
      loadError: null,
      markers: [],
      activeFilters: null,
      colorByField: 'sub_network',
      colorByOptions: COLOR_BY_OPTIONS,
      sizeByField: null,
      sizeByOptions: SIZE_BY_OPTIONS,
      colorOverrides: {},
      tileStyleIndex: 0,
      tileStyles: TILE_STYLES,
      grayscale: false,
      isFullscreen: false,
      showPracticeList: false,
      includeTerminated: false,
      searchQuery: '',
      searchFocused: false,
      searchHighlight: 0,
      svg: null,
      projection: null,
      zoom: null,
      tooltip: null,
      currentTransform: d3.zoomIdentity,
      debouncedSearchQuery: '',
    };
  },
  computed: {
    filteredMarkers() {
      if (!this.activeFilters) return this.markers;
      const f = this.activeFilters;
      // Each dimension is ANDed together; within a multi-select, selections are
      // ORed. Null/unset values are matched explicitly (the filter can hold null).
      const matches = (selected, value) => {
        if (!selected.length) return true;
        const v = value == null || value === '' ? null : value;
        return selected.includes(v);
      };
      return this.markers.filter((m) => {
        // "Primary" => primary locations only; "All" => primary + additional.
        if (f.location_type === 'Primary' && m.location_type !== 'primary') return false;
        if (!matches(f.sub_network, m.sub_network)) return false;
        if (!matches(f.market, m.market)) return false;
        if (!matches(f.practice_tier, m.practice_tier)) return false;
        if (!matches(f.pl_owner_name, m.pl_owner_name)) return false;
        if (!matches(f.implementation_status, m.implementation_status)) return false;
        if (!matches(f.participation_status, m.participation_status)) return false;
        if (f.engagement_score.length) {
          // Buttons are integer buckets 1-5. Selecting x matches scores in [x, x+1):
          // e.g. selecting 3 shows 3.0-3.9. Floor (not round) so a score is never
          // matched to a bucket below its integer part.
          if (m.engagement_score == null) return false;
          const bucket = Math.floor(m.engagement_score);
          if (!f.engagement_score.includes(bucket)) return false;
        }
        if (f.contracts.length) {
          const mc = m.contracts || [];
          // null selection ("No contract") matches practices with no contracts.
          const matchNull = f.contracts.includes(null) && mc.length === 0;
          const matchVal = f.contracts.some((c) => c != null && mc.includes(c));
          if (!matchNull && !matchVal) return false;
        }
        return true;
      });
    },
    colorScale() {
      return buildColorScale(this.markers, this.colorByField);
    },
    legendItems() {
      return this.colorScale.domain().map((label) => ({
        // domain values are already display labels (see buildColorScale)
        label,
        color: this.colorOverrides[label] || this.colorScale(label),
      }));
    },
    hasColorOverrides() {
      return Object.keys(this.colorOverrides).length > 0;
    },
    sortedFilteredMarkers() {
      return [...this.filteredMarkers].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''))
      );
    },
    activeTileUrl() {
      const key = import.meta.env.VITE_CARTO_BASEMAP_KEY;
      return key ? `${TILE_STYLES[this.tileStyleIndex].url}?key=${key}` : TILE_STYLES[this.tileStyleIndex].url;
    },
    showSearchDropdown() {
      return this.searchFocused && this.searchQuery.trim().length >= 2 && this.debouncedSearchQuery.trim().length >= 2;
    },
    searchResults() {
      const q = this.debouncedSearchQuery.toLowerCase().trim();
      if (!q) return [];
      return this.filteredMarkers
        .filter(
          (m) =>
            m.name?.toLowerCase().includes(q) ||
            m.legal_name?.toLowerCase().includes(q) ||
            m.dba?.toLowerCase().includes(q) ||
            m.address?.toLowerCase().includes(q)
        )
        .slice(0, 15);
    },
    sizeScale() {
      if (!this.sizeByField) return null;
      const vals = this.markers.map((m) => m[this.sizeByField]).filter((v) => v != null && v > 0);
      if (!vals.length) return null;
      return d3.scaleSqrt().domain([d3.min(vals), d3.max(vals)]).range([4, 14]);
    },
  },
  watch: {
    filteredMarkers() {
      this.renderMarkers();
    },
    colorByField() {
      this.loadColorOverrides();
      this.renderMarkers();
    },
    sizeByField() {
      this.renderMarkers();
    },
    tileStyleIndex() {
      this.refreshTiles();
    },
    includeTerminated() {
      this.fetchData();
    },
    searchQuery(val) {
      this.searchHighlight = 0;
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = val;
      }, 150);
    },
  },
  created() {
    this.resizeObserver = null;
    this.searchDebounceTimer = null;
    this.tooltipCloseHandler = null;
  },
  mounted() {
    if (!import.meta.env.VITE_CARTO_BASEMAP_KEY) {
      console.error('VITE_CARTO_BASEMAP_KEY is not set; CARTO tiles will be watermarked');
    }
    this.loadColorOverrides();
    this.fetchData();
    this._onKeydown = (e) => {
      if (e.key === 'Escape' && this.isFullscreen) this.isFullscreen = false;
    };
    document.addEventListener('keydown', this._onKeydown);
  },
  beforeUnmount() {
    if (this.tooltip) this.tooltip.remove();
    if (this.tooltipCloseHandler) {
      document.removeEventListener('click', this.tooltipCloseHandler);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    clearTimeout(this.searchDebounceTimer);
    document.removeEventListener('keydown', this._onKeydown);
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.loadError = null;
      try {
        const params = {};
        // Backend excludes terminated practices by default; opt in when toggled on.
        if (this.includeTerminated) params.include_terminated = true;
        const { data } = await axios.get('/api/entities/network-map/', { params });
        this.markers = (data.results || data).map((m) => ({
          ...m,
          pl_owner_name: m.pl_owner?.name || m.pl_owner_name || null,
        }));
      } catch (err) {
        console.error('Failed to load network map data', err);
        this.markers = [];
        this.loadError = 'Failed to load practice data. Check your connection and try again.';
      } finally {
        this.loading = false;
        this.$nextTick(() => this.initMap());
      }
    },

    initMap() {
      const container = this.$refs.mapContainer;
      if (!container) return;

      // Tear down any prior map before rebuilding. fetchData() calls initMap()
      // on every data refetch (e.g. toggling "Terminated"); without this each
      // toggle stacked another <svg> + zoom binding + ResizeObserver, leaking
      // DOM and observers until the map froze.
      this.hideTooltip();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.svg) {
        this.svg.on('.zoom', null);
        this.svg.remove();
        this.svg = null;
      }
      if (this.tooltip) {
        this.tooltip.remove();
        this.tooltip = null;
      }
      d3.select(container).selectAll('svg, .network-map-tooltip').remove();

      const width = container.clientWidth;
      const height = container.clientHeight;

      this.projection = d3.geoMercator().scale(1 / (2 * Math.PI)).translate([0, 0]);

      this.zoom = d3
        .zoom()
        .scaleExtent([1 << 10, 1 << 24])
        .on('zoom', (event) => {
          this.currentTransform = event.transform;
          this.hideTooltip();
          this.onZoom(event);
        });

      this.svg = d3
        .select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('class', 'network-map-svg');

      this.svg.append('g').attr('class', 'tiles');
      this.svg.append('g').attr('class', 'markers');

      this.tooltip = d3
        .select(container)
        .append('div')
        .attr('class', 'network-map-tooltip')
        .style('position', 'absolute')
        .style('display', 'none')
        .style('background', 'white')
        .style('border', '1px solid #e5e7eb')
        .style('border-radius', '8px')
        .style('padding', '12px')
        .style('font-size', '12px')
        .style('box-shadow', '0 4px 12px rgba(0,0,0,0.1)')
        .style('pointer-events', 'auto')
        .style('z-index', '50')
        .style('max-width', '260px');

      this.svg.call(this.zoom);

      this.resizeObserver = new ResizeObserver(() => {
        const w = container.clientWidth;
        const h = container.clientHeight;
        if (this.svg) {
          this.svg.attr('width', w).attr('height', h);
          if (this.currentTransform !== d3.zoomIdentity) {
            this.onZoom({ transform: this.currentTransform });
          }
        }
      });
      this.resizeObserver.observe(container);

      this.fitBounds();
    },

    fitBounds() {
      const pts = this.filteredMarkers.filter((m) => m.latitude && m.longitude);
      if (!pts.length) return;

      const container = this.$refs.mapContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      const lngs = pts.map((m) => m.longitude);
      const lats = pts.map((m) => m.latitude);
      const sw = [d3.min(lngs), d3.min(lats)];
      const ne = [d3.max(lngs), d3.max(lats)];

      const p0 = this.projection(sw);
      const p1 = this.projection(ne);

      const dx = p1[0] - p0[0];
      const dy = p0[1] - p1[1];
      const padding = 80;

      const scale = Math.min(
        (width - padding * 2) / Math.max(dx, 1e-6),
        (height - padding * 2) / Math.max(dy, 1e-6)
      );

      const cx = (p0[0] + p1[0]) / 2;
      const cy = (p0[1] + p1[1]) / 2;

      const transform = d3.zoomIdentity.translate(width / 2, height / 2).scale(scale).translate(-cx, -cy);

      this.svg.transition().duration(750).call(this.zoom.transform, transform);
    },

    onZoom(event) {
      const transform = event.transform;
      const container = this.$refs.mapContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      const tiles = d3Tile()
        .size([width, height])
        .scale(transform.k)
        .translate([transform.x, transform.y])();

      const tileUrl = this.activeTileUrl;
      const tileGroup = this.svg.select('.tiles');
      const image = tileGroup.selectAll('image').data(tiles, (d) => `${d[0]}-${d[1]}-${d[2]}`);

      image.exit().remove();

      image
        .enter()
        .append('image')
        .attr('xlink:href', (d) => tileUrl.replace('{z}', d[2]).replace('{x}', d[0]).replace('{y}', d[1]))
        .attr('x', (d) => (d[0] + tiles.translate[0]) * tiles.scale)
        .attr('y', (d) => (d[1] + tiles.translate[1]) * tiles.scale)
        .attr('width', tiles.scale)
        .attr('height', tiles.scale);

      image
        .attr('x', (d) => (d[0] + tiles.translate[0]) * tiles.scale)
        .attr('y', (d) => (d[1] + tiles.translate[1]) * tiles.scale)
        .attr('width', tiles.scale)
        .attr('height', tiles.scale);

      this.renderMarkers(transform, { animate: false });
    },

    refreshTiles() {
      if (!this.svg) return;
      this.svg.select('.tiles').selectAll('image').remove();
      if (this.currentTransform !== d3.zoomIdentity) {
        this.onZoom({ transform: this.currentTransform });
      }
    },

    renderMarkers(transform, { animate = true } = {}) {
      transform = transform || this.currentTransform;
      if (!this.svg) return;

      const field = this.colorByField;
      const colorFor = this.colorFor;
      const proj = this.projection;
      const sizeScale = this.sizeScale;
      const sizeField = this.sizeByField;

      const data = this.filteredMarkers.filter((m) => m.latitude && m.longitude);
      const zoomLevel = Math.log2(transform.k);
      const clustered =
        zoomLevel < 14
          ? this.clusterMarkers(data, transform)
          : data.map((m) => ({ ...m, _count: 1 }));

      const group = this.svg.select('.markers');

      const items = group
        .selectAll('.marker-group')
        .data(clustered, (d) => (d._count > 1 ? `cluster-${d._gridKey}` : `m-${d.entity_id}`));

      items.exit().transition().duration(200).attr('opacity', 0).remove();

      const self = this;
      const enterG = items
        .enter()
        .append('g')
        .attr('class', 'marker-group')
        .attr('cursor', 'pointer')
        .attr('opacity', 0)
        .on('click', function (event, d) {
          event.stopPropagation();
          if (d._count > 1) {
            self.zoomToCluster(d);
          } else {
            self.showTooltip(event, d);
          }
        })
        .on('mouseenter', function () {
          d3.select(this).select('.marker-ring').attr('opacity', 1);
        })
        .on('mouseleave', function () {
          d3.select(this).select('.marker-ring').attr('opacity', 0);
        });

      enterG
        .append('circle')
        .attr('class', 'marker-ring')
        .attr('fill', 'none')
        .attr('stroke', '#374151')
        .attr('stroke-width', 1.5)
        .attr('opacity', 0);

      enterG.append('circle').attr('class', 'marker-dot');

      enterG
        .append('text')
        .attr('class', 'cluster-label')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('fill', 'white')
        .attr('font-size', '9px')
        .attr('font-weight', '600')
        .attr('pointer-events', 'none');

      const merged = enterG.merge(items);

      if (animate) {
        merged.transition().duration(300).attr('opacity', 1);
      } else {
        merged.attr('opacity', 1);
      }

      merged.attr('transform', (d) => {
        const pt = proj([d.longitude, d.latitude]);
        const sp = transform.apply(pt);
        return `translate(${sp[0]},${sp[1]})`;
      });

      merged.each(function (d) {
        const g = d3.select(this);
        const isCluster = d._count > 1;

        let r;
        if (isCluster) {
          r = Math.min(8 + Math.sqrt(d._count) * 2, 22);
        } else if (sizeScale && d[sizeField] != null) {
          r = sizeScale(d[sizeField]);
        } else {
          r = 6;
        }

        g.select('.marker-ring').attr('r', r + 3);

        const isAdditional = !isCluster && d.location_type === 'additional';
        const isTerminated = !isCluster && d.is_terminated;
        const baseColor = isCluster ? '#6B7280' : colorFor(d[field]) || '#9CA3AF';
        // Standardize bubble STYLE (all solid, white outline). Differentiate
        // Primary vs Additional by COLOR instead: additional locations render as
        // a lightened tint of the same category color. Terminated still gets the
        // red outline so it stays distinguishable.
        g.select('.marker-dot')
          .attr('r', r)
          .attr('fill', isAdditional ? self.lightenColor(baseColor, 0.5) : baseColor)
          .attr('stroke', isTerminated ? '#DC2626' : 'white')
          .attr('stroke-width', isTerminated ? 2 : 1.5)
          .attr('stroke-dasharray', null)
          .attr('opacity', isCluster ? 0.7 : 0.85);

        g.select('.cluster-label').text(isCluster ? d._count : '');
      });
    },

    clusterMarkers(data, transform) {
      const proj = this.projection;
      const gridSize = 40;
      const grid = {};

      data.forEach((m) => {
        const pt = proj([m.longitude, m.latitude]);
        const screen = transform.apply(pt);
        const key = `${Math.floor(screen[0] / gridSize)}_${Math.floor(screen[1] / gridSize)}`;
        if (!grid[key]) {
          grid[key] = { latitude: 0, longitude: 0, _count: 0, _items: [], _gridKey: key };
        }
        grid[key].latitude += m.latitude;
        grid[key].longitude += m.longitude;
        grid[key]._count++;
        grid[key]._items.push(m);
      });

      return Object.values(grid).map((g) => ({
        ...g._items[0],
        latitude: g.latitude / g._count,
        longitude: g.longitude / g._count,
        _count: g._count,
        _items: g._items,
        _gridKey: g._gridKey,
      }));
    },

    zoomToCluster(cluster) {
      const container = this.$refs.mapContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const pt = this.projection([cluster.longitude, cluster.latitude]);
      const newScale = this.currentTransform.k * 2;

      const transform = d3.zoomIdentity
        .translate(width / 2, height / 2)
        .scale(newScale)
        .translate(-pt[0], -pt[1]);

      this.svg.transition().duration(500).call(this.zoom.transform, transform);
    },

    navigateToMarker(marker) {
      if (!marker || !marker.longitude || !marker.latitude) return;
      const container = this.$refs.mapContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;
      const pt = this.projection([marker.longitude, marker.latitude]);
      const targetScale = 1 << 18;

      const transform = d3.zoomIdentity
        .translate(width / 2, height / 2)
        .scale(targetScale)
        .translate(-pt[0], -pt[1]);

      this.svg
        .transition()
        .duration(750)
        .call(this.zoom.transform, transform)
        .on('end', () => {
          const screenPt = transform.apply(pt);
          this.showTooltipAtPosition(screenPt[0] + 12, screenPt[1] - 10, marker);
        });
    },

    jumpToSearchResult(marker) {
      this.searchFocused = false;
      this.searchQuery = '';
      this.navigateToMarker(marker);
    },

    clearSearch() {
      clearTimeout(this.searchDebounceTimer);
      this.searchQuery = '';
      this.debouncedSearchQuery = '';
      this.searchFocused = false;
    },

    onSearchBlur(event) {
      if (this.$refs.searchWrapper?.contains(event.relatedTarget)) return;
      this.searchFocused = false;
    },

    highlightMatch(text) {
      if (!text) return '';
      const q = this.debouncedSearchQuery.trim();
      if (!q) return this.esc(text);
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');
      return this.esc(text).replace(regex, '<mark class="bg-yellow-200 text-gray-900 rounded-sm px-0.5">$1</mark>');
    },

    onSearchEnter() {
      if (!this.searchResults.length) return;
      this.jumpToSearchResult(this.searchResults[this.searchHighlight]);
    },

    showTooltip(event, marker) {
      const container = this.$refs.mapContainer;
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left + 12;
      const y = event.clientY - rect.top - 10;
      this.showTooltipAtPosition(x, y, marker);
    },

    showTooltipAtPosition(x, y, marker) {
      if (this.tooltipCloseHandler) {
        document.removeEventListener('click', this.tooltipCloseHandler);
      }

      this.tooltip
        .style('display', 'block')
        .style('left', `${x}px`)
        .style('top', `${y}px`)
        .html(
          `<div style="line-height:1.5">
            <div style="font-weight:600;color:#111827;display:flex;align-items:baseline;gap:5px;flex-wrap:wrap">
              <span>${this.esc(marker.name)}</span>
              ${marker.is_terminated ? '<span style="color:#dc2626;font-weight:500;font-size:11px">(Terminated)</span>' : ''}
              ${marker.location_type === 'additional' ? '<span style="color:#9ca3af;font-size:10px;font-weight:400;background:#f3f4f6;padding:1px 6px;border-radius:4px">Additional location</span>' : ''}
            </div>
            ${this.showLegalName(marker) ? `<div style="color:#6b7280"><span style="color:#9ca3af">Legal Name:</span> ${this.esc(marker.legal_name)}</div>` : ''}
            ${this.showDba(marker) ? `<div style="color:#6b7280"><span style="color:#9ca3af">DBA:</span> ${this.esc(marker.dba)}</div>` : ''}
            ${this.showAddress(marker) ? `<div style="color:#6b7280">${this.esc(marker.address)}</div>` : ''}
            ${marker.pl_owner_name ? `<div style="color:#6b7280"><span style="color:#9ca3af">PL Owner:</span> ${this.esc(marker.pl_owner_name)}</div>` : ''}
            ${marker.engagement_score != null ? `<div style="color:#6b7280"><span style="color:#9ca3af">Engagement:</span> ${marker.engagement_score}/5</div>` : ''}
            <a href="/entity/${marker.entity_id}" style="display:inline-block;margin-top:8px;color:#F76734;font-size:12px;font-weight:500;text-decoration:none">
              View Practice &rarr;
            </a>
          </div>`
        );

      const container = this.$refs.mapContainer;
      const tooltipNode = this.tooltip.node();
      this.$nextTick(() => {
        const cRect = container.getBoundingClientRect();
        const tRect = tooltipNode.getBoundingClientRect();
        const relRight = tRect.right - cRect.left;
        const relBottom = tRect.bottom - cRect.top;
        if (relRight > cRect.width) {
          this.tooltip.style('left', `${x - tRect.width - 24}px`);
        }
        if (relBottom > cRect.height) {
          this.tooltip.style('top', `${y - tRect.height - 20}px`);
        }
      });

      this.tooltipCloseHandler = (e) => {
        if (!tooltipNode.contains(e.target)) {
          this.tooltip.style('display', 'none');
          document.removeEventListener('click', this.tooltipCloseHandler);
          this.tooltipCloseHandler = null;
        }
      };
      setTimeout(() => document.addEventListener('click', this.tooltipCloseHandler), 0);
    },

    esc(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },

    normalizedEquals(a, b) {
      const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
      return norm(a) === norm(b);
    },

    // Many practices are named by their address; hide the address line when it
    // duplicates the name so the tooltip doesn't show the same string twice.
    showAddress(marker) {
      if (!marker.address) return false;
      return !this.normalizedEquals(marker.address, marker.name);
    },

    // Most practices have dba == name; only show the DBA line when it differs.
    showDba(marker) {
      if (!marker.dba) return false;
      return !this.normalizedEquals(marker.dba, marker.name);
    },

    // Show the legal name line only when it is present and distinct from the display name.
    showLegalName(marker) {
      if (!marker.legal_name) return false;
      return !this.normalizedEquals(marker.legal_name, marker.name);
    },

    hideTooltip() {
      if (this.tooltip) this.tooltip.style('display', 'none');
      if (this.tooltipCloseHandler) {
        document.removeEventListener('click', this.tooltipCloseHandler);
        this.tooltipCloseHandler = null;
      }
    },

    onFiltersChange(filters) {
      this.activeFilters = JSON.parse(JSON.stringify(filters));
      this.$nextTick(() => {
        if (this.filteredMarkers.length && this.svg) {
          this.fitBounds();
        }
      });
    },

    clearAllFilters() {
      this.$refs.filtersPanel?.clearAll();
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      this.$nextTick(() => this.fitBounds());
    },

    async exportPng() {
      const svgEl = this.$refs.mapContainer.querySelector('svg');
      if (!svgEl) return;
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;

      const canvas = document.createElement('canvas');
      const width = parseInt(svgEl.getAttribute('width'));
      const height = parseInt(svgEl.getAttribute('height'));
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, width, height);

      const images = svgEl.querySelectorAll('.tiles image');
      const tilePromises = Array.from(images).map(
        (img) =>
          new Promise((resolve) => {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => {
              ctx.drawImage(
                image,
                parseFloat(img.getAttribute('x')),
                parseFloat(img.getAttribute('y')),
                parseFloat(img.getAttribute('width')),
                parseFloat(img.getAttribute('height'))
              );
              resolve();
            };
            image.onerror = () => resolve();
            image.src =
              img.getAttribute('href') || img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
          })
      );

      await Promise.all(tilePromises);

      const groups = svgEl.querySelectorAll('.marker-group');
      groups.forEach((g) => {
        const tf = g.getAttribute('transform');
        const match = tf?.match(/translate\(([^,]+),([^)]+)\)/);
        if (!match) return;
        const tx = parseFloat(match[1]);
        const ty = parseFloat(match[2]);

        ctx.save();
        ctx.translate(tx, ty);

        const dot = g.querySelector('.marker-dot');
        if (dot) {
          const r = parseFloat(dot.getAttribute('r'));
          ctx.beginPath();
          ctx.arc(0, 0, r, 0, 2 * Math.PI);
          ctx.fillStyle = dot.getAttribute('fill');
          ctx.globalAlpha = parseFloat(dot.getAttribute('opacity') || 1);
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }

        const label = g.querySelector('.cluster-label');
        if (label && label.textContent) {
          ctx.fillStyle = 'white';
          ctx.font = '600 9px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(label.textContent, 0, 0);
        }

        ctx.restore();
      });

      if (this.grayscale) {
        const imageData = ctx.getImageData(0, 0, width, height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          const gray = px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
          px[i] = px[i + 1] = px[i + 2] = gray;
        }
        ctx.putImageData(imageData, 0, 0);
      }

      const attrText = '© OpenStreetMap contributors, © CARTO';
      ctx.font = '10px sans-serif';
      const textMetrics = ctx.measureText(attrText);
      const attrPad = 4;
      const attrX = 8;
      const attrH = 16;
      const attrY = height - attrH - 8;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillRect(attrX, attrY, textMetrics.width + attrPad * 2, attrH);
      ctx.fillStyle = '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(attrText, attrX + attrPad, attrY + attrH / 2);

      if (!ticket.confirm()) return;
      const link = document.createElement('a');
      link.download = 'network-map.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    },

    // --- Bubble color editing ---------------------------------------------
    colorStorageKey() {
      const tenant = localStorage.getItem('current_tenant_slug') || 'default';
      return `${COLOR_OVERRIDE_PREFIX}_${tenant}_${this.colorByField}`;
    },

    colorFor(rawVal) {
      const label = labelFor(this.colorByField, rawVal);
      if (this.colorOverrides[label]) return this.colorOverrides[label];
      return this.colorScale(label);
    },

    // Blend a color toward white by `amount` (0 = unchanged, 1 = white). Used to
    // render "additional" location bubbles as a lighter tint of their category
    // color, so Primary vs Additional reads by color rather than outline style.
    lightenColor(color, amount) {
      const c = d3.color(color);
      if (!c) return color;
      const rgb = c.rgb();
      rgb.r = Math.round(rgb.r + (255 - rgb.r) * amount);
      rgb.g = Math.round(rgb.g + (255 - rgb.g) * amount);
      rgb.b = Math.round(rgb.b + (255 - rgb.b) * amount);
      return rgb.formatHex();
    },

    loadColorOverrides() {
      try {
        const raw = localStorage.getItem(this.colorStorageKey());
        this.colorOverrides = raw ? JSON.parse(raw) : {};
      } catch {
        this.colorOverrides = {};
      }
    },

    setColorOverride(label, color) {
      this.colorOverrides = { ...this.colorOverrides, [label]: color };
      try {
        localStorage.setItem(this.colorStorageKey(), JSON.stringify(this.colorOverrides));
      } catch {
        // localStorage unavailable / quota — overrides still apply in-session
      }
      this.renderMarkers();
    },

    onLegendColorInput(item, event) {
      this.setColorOverride(item.label, event.target.value);
    },

    resetColors() {
      this.colorOverrides = {};
      try {
        localStorage.removeItem(this.colorStorageKey());
      } catch {
        // no-op
      }
      this.renderMarkers();
    },

    // --- Practice list export (currently displayed markers) ---------------
    csvCell(val) {
      const s = val == null ? '' : String(val);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    },

    exportPracticeList() {
      if (blockSensitiveEgress('export')) return;
      const rows = [
        [
          'Practice Name',
          'Legal Name',
          'DBA',
          'TIN',
          'Market',
          'Sub-network',
          'Practice Tier',
          'PL Owner',
          'Contract',
          'Implementation Status',
          'Participation Status',
          'Engagement Score',
          'Attribution',
        ],
      ];
      this.sortedFilteredMarkers.forEach((m) =>
        rows.push([
          m.name || '',
          m.legal_name || '',
          m.dba || '',
          m.tin || '',
          m.market || '',
          m.sub_network || '',
          m.practice_tier || '',
          m.pl_owner_name || '',
          (m.contracts || []).join('; '),
          m.implementation_status || '',
          m.participation_status || '',
          m.engagement_score == null ? '' : m.engagement_score,
          m.attribution == null ? '' : m.attribution,
        ])
      );
      const csv = rows.map((r) => r.map((c) => this.csvCell(c)).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = 'network-map-practices.csv';
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
